import { isCalifornia, isColorado, isArkansas, isEnLanguage } from ".";
import { shift_months, getDayDelta, shift_days } from "../datetime";
import { getCountryCode } from "../meristemContext";
import { ServerContextState } from "../redux/slices/serverContext";
import getStore from "../redux/store";
import { CancellationReasonOptions } from "src/pages/subscription/[context]/cancellationReason";
import { isSubscriptionPremiumTier } from "@utils/userSegment/features";
import { getSessionState } from "@pageDefinitions/session";
import {
  getMedSKUByProductName,
  isBrandedMedTier,
  isMedTierPlanId,
  isPremiumTierPlanId,
} from "@utils/plans";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { checkSegments } from "./util";
import { getEligibleMedProducts } from "@utils/medEligibility";
import { loadPendingSwitchData } from "../redux/slices/billingData";

export const isSubscriptionInvalid = () => {
  const { serverContext } = getStore().getState();
  return (
    serverContext.result !== "success" ||
    (serverContext.subscription_status !== "ACTIVE" &&
      serverContext.subscription_status !== "PAST_DUE") ||
    serverContext.has_expiring_subscription
  );
};

export const isEligibleForClickToCancel = checkSegments({
  oneOf: [isCalifornia, isColorado, isArkansas],
});

export function showDisplayTimeLeftPage() {
  const { serverContext, userData } = getStore().getState();
  const nextBillingDate = new Date(serverContext.expiry_time);
  const planDuration = serverContext.plan_duration;
  const previousBillingDate = shift_months(-planDuration, nextBillingDate);

  const daysCompleted = getDayDelta(previousBillingDate, new Date());
  const planNrOfDays = getDayDelta(previousBillingDate, nextBillingDate);
  const progress = daysCompleted / planNrOfDays;

  const country = serverContext.billing_country || getCountryCode();

  if (
    userData.billing_reminder?.optIn || // if the user already opted in don't show the page again
    isEligibleForClickToCancel() || // the page isn't compliant with CA/CO/AR cancel flow
    serverContext.is_in_trial ||
    progress > 0.75 ||
    serverContext.subscription_is_hm ||
    serverContext.subscription_status !== "ACTIVE" ||
    country !== "US" ||
    isMedTierPlanId(serverContext.current_plan_id)
  ) {
    return false;
  }
  return true;
}

export function showDisplayTimeLeftPageTrialists() {
  const { serverContext, userData } = getStore().getState();
  const {
    trial_duration: trialDuration,
    expiry_time: expiryTime,
    current_plan_id: planId,
  } = serverContext;
  const subscriptionStartDate = new Date(expiryTime);
  const trialStartDate = shift_days(-trialDuration, subscriptionStartDate);
  const now = new Date();
  // NOTE: not using getDayDelta since we want the exact date difference, not rounded
  //       because the billing reminder is sent on Day 3 (or 10 for 15 day trials)
  const daysCompleted =
    (now.getTime() - trialStartDate.getTime()) / (1000 * 60 * 60 * 24);

  const country = serverContext.billing_country || getCountryCode();
  const dateIsEligible =
    (trialDuration <= 8 && daysCompleted <= 3) ||
    (trialDuration > 8 && daysCompleted <= 10);

  return (
    serverContext.is_in_trial &&
    !userData.billing_reminder?.trialOptIn &&
    !isEligibleForClickToCancel() &&
    country === "US" &&
    isEnLanguage() &&
    dateIsEligible &&
    !isMedTierPlanId(planId)
  );
}

function getCancellationReason(): CancellationReasonOptions | undefined {
  const { currentAnswers } = getStore().getState();
  const counterOfferAnswers =
    currentAnswers.questionAnswers?.["userData.counterOfferAnswers"];
  return counterOfferAnswers?.cancellationReason?.[0];
}

/**
 * Determines if a user is ineligible for counter offers.
 *
 * Eligibility Rules:
 *
 * Common Ineligibility Criteria:
 * - VIP users (old business rule)
 * - Invalid subscriptions
 * - Users ineligible for med offers (when on med tier plans)
 *
 * Non-BMS Subscription Specific:
 * - Users with pending counteroffer OR who have accepted a counteroffer
 * - Users with pending subscription (unless it's a BMS migration)
 * - Users not eligible for counter offers (growth side checks)
 * - PAST_DUE subscription status (only ACTIVE subscriptions are eligible)
 *
 * BMS Subscription Specific:
 * - Users not eligible for counter offers (billing core side logic)
 *
 * @returns {boolean} True if user is ineligible for counter offers
 */
export function isIneligibleForCounterOffer() {
  const { serverContext, userData } = getStore().getState();
  const {
    eligible_for_counter_offer,
    is_eligible_for_med_offers,
    has_pending_subscription,
    has_pending_bms_migration,
    subscription_statuses,
    subscription_status,
    current_plan_id,
  } = serverContext;

  const isIneligibleForMedOffers =
    !is_eligible_for_med_offers &&
    isMedTierPlanId(serverContext.current_plan_id);

  // For b2b users who are on med plans, we want them to get into the counter offer flow
  // in order to collect the survey answer questions. They will not be offered
  // an actual counter or discount offer.
  if (isB2BUser() && isMedTierPlanId(current_plan_id)) {
    return false;
  }

  const checkIsIneligibleForNonBmsSubs =
    userData.vip ||
    subscription_statuses == null ||
    subscription_statuses.includes("COUNTEROFFER") ||
    (has_pending_subscription && !has_pending_bms_migration) ||
    !eligible_for_counter_offer ||
    subscription_status === "PAST_DUE" ||
    isSubscriptionInvalid() ||
    isIneligibleForMedOffers;

  const checkIsIneligibleForBmsSubs =
    userData.vip ||
    !eligible_for_counter_offer ||
    isSubscriptionInvalid() ||
    isIneligibleForMedOffers;

  return userData.growthPaymentSource === "BMS"
    ? checkIsIneligibleForBmsSubs
    : checkIsIneligibleForNonBmsSubs;
}

function isEligibleForPercentDiscountBase() {
  const { serverContext } = getStore().getState();
  return isEnLanguage() && serverContext.eligible_for_percent_discount_offer;
}

export function isEligibleForPercentDiscount() {
  return (
    isEligibleForPercentDiscountBase() &&
    getCancellationReason() === "expensive"
  );
}

export function isEligibleForPercentDiscountPost() {
  return (
    isEligibleForPercentDiscountBase() &&
    getCancellationReason() !== "expensive" &&
    getCancellationReason() !== "notSatisfiedPremium"
  );
}

export function isEligibleForTrialExtension() {
  const { serverContext } = getStore().getState();
  return (
    getCancellationReason() === "trialTime" &&
    !serverContext.subscription_statuses.includes("TRIAL_EXTENSION") &&
    serverContext.is_in_trial &&
    !isPremiumTierPlanId(serverContext.current_plan_id)
  );
}

function isEligibleForPauseBase() {
  const { serverContext } = getStore().getState();
  return !hasAcceptedPause(serverContext);
}

export function isEligibleForPause() {
  const cancellationReason = getCancellationReason();
  return (
    isEligibleForPauseBase() &&
    cancellationReason !== "trialTime" &&
    cancellationReason !== "expensive" &&
    cancellationReason !== "notSatisfiedPremium"
  );
}

export function isEligibleForPauseDiscounted() {
  return (
    isEligibleForPauseBase() &&
    !isEligibleForPause() &&
    getCancellationReason() === "expensive" &&
    !isSubscriptionPremiumTier()
  );
}

function hasAcceptedPause(serverContext: ServerContextState) {
  return serverContext.subscription_statuses.includes("PAUSE_SUBSCRIPTION");
}

export function isEligibleForPremiumDowngrade() {
  return isSubscriptionPremiumTier();
}

export function isEligibleForMedUpgrade() {
  const { serverContext } = getStore().getState();
  const cancellationReason = getCancellationReason();
  return (
    serverContext.billing_country === "US" &&
    serverContext.is_eligible_for_med_upgrade &&
    ["overwhelming", "notSatisfiedPremium", "noResults", "expensive"].includes(
      cancellationReason
    ) &&
    getEligibleMedProducts({
      checkBMI: false,
      checkHealthRisks: false,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
    }).length > 0
  );
}

export const isMedBrandedUser = (state = getStore().getState()) => {
  const { serverContext } = state;
  return isBrandedMedTier(serverContext?.current_plan_id);
};

export const isEligibleForMedDiscountCounteroffer = (
  state = getStore().getState()
) => {
  const { medCancellationReasons } = state.counterOffer;

  if (isB2BUser()) {
    return false;
  }

  return medCancellationReasons?.some((item) =>
    [
      "EXPENSIVE",
      "OTHER",
      "PRESCRIPTION_ISSUES",
      "FOUND_ALTERNATIVE",
      "NOT_ENOUGH_SUPPORT",
    ].includes(item)
  );
};

export const isEligibleForMedDowngradeCounteroffer = (
  state = getStore().getState()
) => {
  const { medCancellationReasons } = state.counterOffer;
  if (isB2BUser()) {
    return false;
  }

  const surveyAnswers = getSurveyAnswers(state);
  return (
    surveyAnswers.cancelMedTier !== "cancelMed" &&
    medCancellationReasons?.some((item) =>
      [
        "EXPENSIVE",
        "OTHER",
        "NOT_LOOKING",
        "SIDE_EFFECTS",
        "RESULTS",
        "ADMINISTERING_COMFORT",
        "FOUND_ALTERNATIVE",
      ].includes(item)
    )
  );
};

export function isMedUpgradeFromCounterOffer() {
  return getSessionState("browser").medUpgradeFromCounterOffer;
}

export const isOnInstallmentPlan = () => {
  const { serverContext } = getStore().getState();
  const { is_on_installments } = serverContext;
  return is_on_installments;
};

export function isB2BUser(): boolean {
  const { serverContext } = getStore().getState();
  return serverContext.is_b2b_user ?? false;
}

export function hasPendingSwitchToMedTier() {
  const productName =
    getStore().getState().billingData.pendingSwitchProductName;
  return productName != null && getMedSKUByProductName(productName) !== null;
}

hasPendingSwitchToMedTier.load = async () => {
  await getStore().dispatch(loadPendingSwitchData());
};

export function hasPendingSwitch() {
  const { billingData } = getStore().getState();
  return !!billingData.pendingSwitchProductName;
}

hasPendingSwitch.load = async () => {
  await getStore().dispatch(loadPendingSwitchData());
};
